import api from '@/api/index';
import companyId from '@/api/modules/companyId';
import { getAppInsights } from '@/applicationInsights';
import User from '@/models/User';
import { ActionTree, Commit, Dispatch, GetterTree, MutationTree } from 'vuex';
import { useAccountStore } from '@/store/modules/token';

export interface UserState {
    user: User;
    roles: string[];
    permissions: string[];
}

export interface RootState {}

const state = (): UserState => ({
    user: new User(),
    roles: [],
    permissions: [],
});

// Getters
const getters: GetterTree<UserState, RootState> = {
    isLoggedIn: (state) =>
        state.user !== null && state.user.id > 0 && (state.user.companyId === null || state.user.active),

    isMasterAdmin: (state, getters) => getters.isLoggedIn && state.user.isMasterAdmin,

    isSuperAdmin: (state, getters) => getters.isLoggedIn && (state.user?.isSuperAdmin ?? false),

    isAdmin: (state, getters) =>
        getters.isUser && (state.roles.includes('Admin') || state.user.role === 'Admin' || getters.isSuperAdmin),

    isPM: (state, getters) => getters.isUser && (state.user.role === 'Project Manager' || getters.isSuperAdmin),

    isUser: (state, getters) => getters.isLoggedIn && state.user.companyId !== null && state.user.companyId > 0,

    isProduction: (state, getters) =>
        getters.isUser &&
        !getters.isAdmin &&
        (state.roles.includes('Production') || getters.hasPermission('Estimator.Access')),

    isCompanyUser: (state, getters) =>
        getters.isLoggedIn &&
        state.user.companyId !== null &&
        state.user.companyId > 0 &&
        (state.roles.includes('User') ||
            state.user.role === 'User' ||
            state.roles.includes('Admin') ||
            state.user.role === 'Admin'),

    hasPermission: (state) => (permission: string) => {
        return state.permissions.includes(permission) || state.user?.permissions?.includes(permission);
    },
};

// Mutations
const mutations: MutationTree<UserState> = {
    setUser(state, user: User | null) {
        if (user !== null && user instanceof User) {
            state.user = user;
            state.roles = user.roles || [];
            state.permissions = user.permissions || [];
        } else {
            state.user = new User();
            state.roles = [];
            state.permissions = [];
        }
        const appInsights = getAppInsights();
        if (appInsights) {
            const userId: string = state.user.id > 0 ? String(state.user.id) : '';
            const companyId: string =
                state.user.companyId !== null && state.user.companyId > 0 ? String(state.user.companyId) : '';
            appInsights.setAuthenticatedUserContext(userId, companyId, false);
        }
    },
    setRoles(state, roles: string[]) {
        state.roles = roles || [];
    },
    setPermissions(state, permissions: string[]) {
        state.permissions = permissions || [];
    },
};

// Actions
const actions: ActionTree<UserState, RootState> = {
    async clear({ commit }: { commit: Commit }) {
        commit('setUser', null);
        commit('setRoles', []);
        commit('setPermissions', []);
        const accountStore = useAccountStore();
        accountStore.reset();
    },
    async refresh({ dispatch, commit }: { dispatch: Dispatch; commit: Commit }) {
        await companyId.setFromIdb();
        const user = await api.users.getSelf();
        commit('setUser', user);
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
